import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Divider,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';

const ProjectMapOverlay = ({ isOpen, onClose, sections, tagMaps, onSegmentClick, currentSegmentIndex }) => {
    const [openPages, setOpenPages] = useState({});

    const groupedSegments = useMemo(() => {
        const groups = {};
        sections.forEach((text, index) => {
            const tm = tagMaps && tagMaps[index] ? tagMaps[index] : {};
            const page = tm._pageContext || 'General';
            if (!groups[page]) {
                groups[page] = [];
            }
            groups[page].push({
                index,
                text: text.length > 60 ? text.substring(0, 60) + '...' : text
            });
        });
        return groups;
    }, [sections, tagMaps]);

    const togglePage = (page) => {
        setOpenPages(prev => ({
            ...prev,
            [page]: !prev[page]
        }));
    };

    if (!isOpen) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: 350,
                height: '100vh',
                bgcolor: 'background.paper',
                boxShadow: 10,
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease-in-out',
                transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon /> Project Map
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                <List component="nav" dense>
                    {Object.keys(groupedSegments).map((page) => (
                        <React.Fragment key={page}>
                            <ListItemButton onClick={() => togglePage(page)}>
                                <ListItemText 
                                    primary={`Page ${page}`} 
                                    primaryTypographyProps={{ fontWeight: 'bold' }}
                                />
                                {openPages[page] ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={openPages[page]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {groupedSegments[page].map((segment) => (
                                        <ListItemButton 
                                            key={segment.index}
                                            sx={{ 
                                                pl: 4,
                                                bgcolor: currentSegmentIndex === segment.index ? 'action.selected' : 'transparent',
                                                borderLeft: currentSegmentIndex === segment.index ? '4px solid #1976d2' : 'none'
                                            }}
                                            onClick={() => onSegmentClick(segment.index)}
                                        >
                                            <DescriptionIcon sx={{ fontSize: 'small', mr: 1, color: 'text.secondary' }} />
                                            <ListItemText 
                                                primary={segment.text || '(Empty segment)'} 
                                                primaryTypographyProps={{ 
                                                    variant: 'body2',
                                                    noWrap: true,
                                                    color: segment.text ? 'text.primary' : 'text.disabled'
                                                }}
                                                secondary={`Segment ${segment.index + 1}`}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>
                            <Divider />
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

export default ProjectMapOverlay;
